import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Checkbox,
  Col,
  Form,
  Input,
  Layout,
  message,
  Modal,
  Row,
  Skeleton,
  Typography,
} from 'antd';
import { get } from 'lodash';
import slugify from 'slugify';

import { PageSpinner } from 'components/page-spinner';
import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';

import CollectionApi from 'services/collection';
import { errHandling } from 'utils';

import 'react-quill-new/dist/quill.snow.css';
import './style.scss';

const { confirm } = Modal;
const { Content } = Layout;
const { Search } = Input;
const { Title } = Typography;

const FIELDS = {
  BANNER_IMAGE: 'banner_image',
  CATEGORIES: 'categories',
  COLLECTION: 'collection',
  DESKTOP_MEDIA_URL: 'image',
  MOBILE_MEDIA_URL: 'mobile_image',
  PRODUCT_LIST: 'collection_items_attributes',
  SHOW_HOME: 'show_in_home',
  SLUG: 'slug',
  TITLE: 'title',
};

export function CollectionCreateEdit() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [allProducts, setAllProducts] = useState([]);
  const [collectionSlug, setCollectionSlug] = useState('');
  const [formLayout] = useState('vertical');
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadModal, setLoadModal] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [productList, setProductList] = useState([]);
  const [productNextPage, setProductNextPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [showInHome, setShowInHome] = useState(false);
  const [value, setValue] = useState({});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;

  const buttonItemLayout =
    formLayout === 'horizontal'
      ? {
          wrapperCol: { span: 14, offset: 4 },
        }
      : null;

  function SortableItem({ index, value: product }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: product.id || index,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      userSelect: 'none',
    };

    return (
      <Row
        ref={setNodeRef}
        style={style}
        {...attributes}
        align="middle"
        className="product-row draggable-item"
        gutter={20}
        type="flex">
        <Col span={1}>
          <div {...listeners} style={{ cursor: 'grab', padding: '0 10px' }}>
            ⋮⋮
          </div>
        </Col>
        <Col span={3} {...listeners}>
          <div
            className="product--picture"
            style={{
              backgroundImage: `url(${get(
                product,
                'preview.secure_url',
                ''
              )}), url(/img/logo/no-image.png)`,
            }}></div>
        </Col>
        <Col span={14} {...listeners}>
          <span className="bold">{get(product, 'title', '')}</span>
        </Col>
        <Col span={3}>
          <PdButton
            full
            onClick={(e) => {
              e.stopPropagation();
              deleteProduct(product.id);
            }}
            outline
            type="danger">
            Delete
          </PdButton>
        </Col>
        <Col span={3}>
          <Link to={`/products/edit?id=${get(product, 'id', '')}`}>
            <PdButton full type="primary">
              Edit
            </PdButton>
          </Link>
        </Col>
      </Row>
    );
  }

  function SortableList({ items, onSortEnd }) {
    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        const oldIndex = items.findIndex((item, index) => (item.id || index) === active.id);
        const newIndex = items.findIndex((item, index) => (item.id || index) === over.id);
        onSortEnd({ oldIndex, newIndex });
      }
    };

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((item, index) => ({ id: item.id || index }))} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableItem index={index} key={item.id} value={item} />
          ))}
        </SortableContext>
      </DndContext>
    );
  }

  async function getCollection() {
    try {
      setLoadingPage(true);
      if (id) {
        const { data } = await CollectionApi.getCollection(id);
        const CollectionDetail = data.data;
        setShowInHome(get(CollectionDetail, FIELDS.SHOW_HOME, false));
        setCollectionSlug(get(CollectionDetail, FIELDS.SLUG, ''));
        setProductList(get(CollectionDetail, 'products', []));
        setValue(CollectionDetail);
        form.setFieldsValue({
          [FIELDS.TITLE]: CollectionDetail.title,
        });
      }
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingPage(false);
    }
  }

  async function showModal() {
    setLoadModal(true);
    setModalVisible(true);

    await fetchAllProduct(productNextPage);
    setLoadModal(false);
  }

  async function fetchAllProduct(
    page = 1,
    q = '',
    loadmore,
    total = 4,
    sort_by = 'created_at desc'
  ) {
    try {
      const { data } = await CollectionApi.getProductList(
        page,
        q,
        sort_by,
        total
      );
      const productData = data.data;
      const metadata = data.metadata;

      const newProduct = productData.map((product) => {
        if (productList.some((e) => e.id === product.id)) {
          product.checked = true;
        } else {
          product.checked = false;
        }
        return product;
      });

      setProductNextPage(metadata.next_cursor);
      if (loadmore) {
        setAllProducts((prevstate) => [...prevstate, ...newProduct]);
      } else {
        setAllProducts(newProduct);
      }
    } catch (err) {
      errHandling(err);
    }
  }

  async function onSortEnd({ newIndex, oldIndex }) {
    let list = [...productList];
    let temp = list[oldIndex];
    list.splice(oldIndex, 1);
    list.splice(newIndex, 0, temp);

    const newList = list.map((item, i) => {
      item.order_level = i;
      return item;
    });

    setProductList(newList);
  }

  async function onFinish(values) {
    try {
      if (collectionSlug !== '') {
        value[FIELDS.SLUG] = collectionSlug;
      } else {
        value[FIELDS.SLUG] = null;
      }
      value[FIELDS.SHOW_HOME] = showInHome;
      values[FIELDS.PRODUCT_LIST] = productList.map((product) => {
        product.product_id = product.id;
        return product;
      });
      setLoadingButton(true);
      if (id) {
        values.weight = Number(values.weight) * 1000;
        await CollectionApi.updateCollection(id, { ...value, ...values });
        message.success('Successfully updated collection');
      } else {
        await CollectionApi.createCollection({ ...value, ...values });
        message.success('Successfully created collection');
      }
      navigate({
        pathname: '/collections',
      });
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingButton(false);
    }
  }

  function changeValue(key, e) {
    if (e) {
      setValue((prevState) => ({
        ...prevState,
        [key]: e,
      }));
    }
  }

  function deleteProduct(id) {
    confirm({
      title: 'Are you sure delete this product?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        const newProductList = productList.filter(
          (product) => Number(get(product, 'id', '')) !== Number(id)
        );
        const withOrderLevel = newProductList.map((item, i) => {
          item.order_level = i;
          return item;
        });
        setProductList(withOrderLevel);
      },
      onCancel() {},
    });
  }

  function changePage(page) {
    setPage(page);
    fetchAllProduct(page, searchValue, true);
  }

  function onChecked(i, e) {
    const newProductList = allProducts.slice(0);
    newProductList[i].checked = e.target.checked;
    setAllProducts(newProductList);
  }

  function submitModal() {
    const newProduct = allProducts
      .slice(0)
      .filter(
        (product) =>
          product.checked &&
          productList.filter((e) => e.id === product.id).length === 0
      );
    const alllist = [...newProduct, ...productList];
    const newProducts = alllist.map((item, i) => {
      item.order_level = i;
      return item;
    });
    setProductList(newProducts);
    exitModal();
  }

  function searchOnClick() {
    fetchAllProduct(1, searchValue);
  }

  function exitModal() {
    setProductNextPage(1);
    setSearchValue('');
    setPage(1);
    setAllProducts([]);
    setModalVisible(false);
  }

  function changeSlug(e) {
    setCollectionSlug(e.target.value);
  }

  function changeShowInHome(e) {
    setShowInHome(e.target.checked);
  }

  function handleChangeValue(url, key) {
    setValue((prevState) => ({
      ...prevState,
      [key]: url,
    }));
  }

  useEffect(() => {
    if (id) {
      getCollection();
    } else {
      setValue([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, id]);

  if (loadingPage) {
    return <PageSpinner />;
  }

  return (
    <Layout className="pd-cms-collection main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">
            {id ? 'Edit' : 'Create New'} Collection
          </div>
          <Content className="pageContent">
            <Form form={form} layout={formLayout} onFinish={onFinish}>
              <div className="section-container">
                <div className="inner-section">
                  <Form.Item
                    label="Title"
                    name={FIELDS.TITLE}
                    {...formItemLayout}
                    rules={[
                      { message: 'Please input Title!', required: true },
                    ]}>
                    <Input
                      onChange={changeValue.bind(this, FIELDS.TITLE)}
                      placeholder="Write collection title here"
                    />
                  </Form.Item>

                  <div className="show-homepage">
                    <span className="text">Show in Homepage</span>
                    <Checkbox
                      checked={showInHome}
                      onChange={changeShowInHome}
                    />
                  </div>

                  <Form.Item label="Slug (optional)" {...formItemLayout}>
                    <Input
                      value={collectionSlug}
                      onChange={changeSlug}
                      placeholder="Write collection slug here"
                    />
                    Collection URL: https://www.homestory.co.id/collections/
                    {slugify(collectionSlug, {
                      remove: /[*+~.()'"`/><!:;?@{}=+]/g,
                    })}
                  </Form.Item>

                  <Form.Item
                    label="Desktop Banner (1200x200 px)"
                    {...formItemLayout}>
                    <PdUploader
                      afterUpload={handleChangeValue}
                      imgClassName="w-90"
                      imgUrl={value[FIELDS.BANNER_IMAGE]}
                      keyValue={FIELDS.BANNER_IMAGE}
                      name="Desktop Banner (1200x200 px)"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Mobile Banner (640x320 px)"
                    {...formItemLayout}>
                    <PdUploader
                      afterUpload={handleChangeValue}
                      imgClassName="w-half"
                      imgUrl={value[FIELDS.MOBILE_MEDIA_URL]}
                      keyValue={FIELDS.MOBILE_MEDIA_URL}
                      name="Mobile Image (640x320 px)"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Desktop Sidebar (330x510 px)"
                    {...formItemLayout}>
                    <PdUploader
                      afterUpload={handleChangeValue}
                      imgClassName="w-30"
                      imgUrl={value[FIELDS.DESKTOP_MEDIA_URL]}
                      keyValue={FIELDS.DESKTOP_MEDIA_URL}
                      name="Desktop Image (330x510 px)"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="section-container">
                <div className="inner-section">
                  <div>
                    <Row gutter={20}>
                      <Col span={20}>
                        <Title className="pageSubtitle" level={3}>
                          Products
                        </Title>
                      </Col>
                      <Col span={4}>
                        <PdButton full onClick={showModal} type="primary">
                          Add Product
                        </PdButton>
                      </Col>
                      <Col
                        className="pd-cms-collection--product-list"
                        span={24}>
                        <div id="drag-content">
                          <SortableList
                            items={productList}
                            onSortEnd={onSortEnd}
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
                <Form.Item {...buttonItemLayout}>
                  <div className="buttonSection mt-40">
                    <PdButton
                      full
                      htmlType="submit"
                      loading={loadingButton}
                      type="primary">
                      Publish
                    </PdButton>
                  </div>
                </Form.Item>
              </div>
            </Form>
          </Content>
        </div>
      </div>
      <Modal
        centered
        className="modal--productList"
        onCancel={exitModal}
        onOk={exitModal}
        open={modalVisible}
        wrapClassName="hidden-wrap-modal">
        {loadModal ? (
          <div className="modal-container">
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : (
          <div className="modal-container">
            <Row>
              <Col className="header" span={24}>
                <h1 className="bold">Add Product</h1>
              </Col>
              <Col span={24}>
                <Search
                  className="search-product"
                  enterButton
                  onChange={(e) => setSearchValue(e.target.value)}
                  onSearch={searchOnClick}
                  placeholder="Search Product List"
                  value={searchValue}
                />
              </Col>
              <Col className="product-catalog" span={24}>
                {allProducts.map((product, i) => (
                  <Row className="product-item" key={i}>
                    <Col span={1}>
                      <Checkbox
                        checked={product.checked}
                        onChange={onChecked.bind(this, i)}
                      />
                    </Col>
                    <Col className="product-title" span={23}>
                      <span className="bold">{product.title}</span>
                    </Col>
                  </Row>
                ))}
                <Row>
                  <Col offset={8} span={8}>
                    <div className="center-button">
                      {productNextPage !== -1 && (
                        <PdButton
                          className="load-more-button"
                          onClick={changePage.bind(this, Number(page) + 1)}
                          outline
                          type="primary">
                          Load More
                        </PdButton>
                      )}
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col className="button-container" offset={7} span={10}>
                <PdButton full onClick={submitModal} type="primary">
                  Add Product
                </PdButton>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </Layout>
  );
}

export default CollectionCreateEdit;
